import { Target, Eye, Heart, Users, Award, Globe, MapPin, Rocket, Lightbulb, Cpu, Code, Stethoscope, ArrowRight, Zap, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function About() {
  const values = [
    { icon: Heart, title: 'Patient First', desc: 'Every decision we make prioritizes patient outcomes and safety above all else.' },
    { icon: Target, title: 'Clinical Accuracy', desc: 'We hold ourselves to the highest standards of clinical validation and accuracy.' },
    { icon: Users, title: 'Collaboration', desc: 'We work alongside healthcare providers, not to replace them, but to empower them.' },
    { icon: Eye, title: 'Transparency', desc: 'Our AI systems provide explainable insights that clinicians can trust and verify.' },
  ];

  const stats = [
    { label: 'Healthcare Partners', value: '500+' },
    { label: 'Patients Served', value: '2M+' },
    { label: 'AI Models Deployed', value: '25+' },
    { label: 'Countries', value: '30+' },
  ];

  const teamExperience = [
    { 
      icon: Stethoscope, 
      title: 'Clinical Expertise', 
      desc: 'Our team includes practicing physicians and clinical researchers who understand the real-world challenges of modern healthcare delivery.',
      highlights: ['Board-certified specialists', 'Active clinical practice', 'Research publications']
    },
    { 
      icon: Cpu, 
      title: 'AI & Machine Learning', 
      desc: 'Deep expertise in healthcare AI, with experience building and deploying ML models at scale for Fortune 500 healthcare companies.',
      highlights: ['PhD-level AI researchers', 'Healthcare AI patents', 'Production ML systems']
    },
    { 
      icon: Code, 
      title: 'Enterprise Software', 
      desc: 'Decades of combined experience building HIPAA-compliant, enterprise-grade healthcare software that serves millions of users.',
      highlights: ['HIPAA/SOC2 compliance', 'High-availability systems', 'EHR integrations']
    },
    { 
      icon: Lightbulb, 
      title: 'Healthcare Innovation', 
      desc: 'Track record of bringing innovative healthcare products from concept to market, with successful exits and industry recognition.',
      highlights: ['FDA clearances', 'Industry awards', 'Successful launches']
    },
  ];

  const futureVision = [
    {
      phase: 'Now',
      title: 'AI Med Analytics',
      status: 'Live',
      desc: 'Our cardiac analytics platform (Cardiolyzer) is already helping healthcare providers detect cardiovascular risks earlier with 99.7% accuracy.',
      color: 'from-cyan-500 to-blue-600'
    },
    {
      phase: 'Q2 2026',
      title: 'SmartMed AI',
      status: 'In Development',
      desc: 'Clinical decision support system providing evidence-based recommendations for diagnosis and treatment planning across multiple specialties.',
      color: 'from-emerald-500 to-teal-600'
    },
    {
      phase: 'Q3 2026',
      title: 'MediBot AI',
      status: 'In Development',
      desc: 'Intelligent patient communication platform handling inquiries, scheduling, and triage 24/7 with human-like understanding.',
      color: 'from-purple-500 to-indigo-600'
    },
    {
      phase: 'Q4 2026',
      title: 'AI Medical Coder',
      status: 'Planning',
      desc: 'Automated medical coding solution ensuring accurate ICD-10 and CPT assignment, reducing claim denials by up to 40%.',
      color: 'from-orange-500 to-red-600'
    },
    {
      phase: '2026',
      title: 'Unified Platform',
      status: 'Vision',
      desc: 'A fully integrated healthcare AI ecosystem where all products work seamlessly together, sharing insights and improving outcomes across the care continuum.',
      color: 'from-pink-500 to-rose-600'
    },
  ];

  return (
    <div className="min-h-screen bg-[#030712]">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[100px]" />
      </div>

      {/* Hero Section */}
      <section className="relative z-10 pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full mb-6">
              <Award className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 text-sm font-medium">About PulseNex AI</span>
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">
              Pioneering the Future of{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Healthcare AI
              </span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              We're a team of physicians, AI researchers, and healthcare technology experts
              united by a single mission: to harness the power of artificial intelligence
              to improve patient outcomes and transform how healthcare is delivered.
            </p>
          </div>
        </div>
      </section>

      {/* Story & Stats Section */}
      <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  PulseNex was born from a simple but powerful observation: healthcare providers
                  are drowning in data but starving for actionable insights. Every day, clinicians
                  make life-or-death decisions under pressure, often without the tools they need
                  to extract meaning from the vast amounts of medical data at their fingertips.
                </p>
                <p>
                  We set out to change that. By combining deep clinical expertise with cutting-edge
                  artificial intelligence, we're building tools that augment—not replace—the
                  brilliant minds of healthcare professionals. Our AI works alongside doctors,
                  helping them make faster, more accurate decisions while reducing burnout.
                </p>
                <p>
                  Today, PulseNex serves hundreds of healthcare organizations worldwide, processing
                  millions of data points daily. But we're just getting started. Our vision is a
                  world where every clinician has an AI partner helping them deliver the best
                  possible care.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 p-6 rounded-2xl text-center hover:border-cyan-500/30 transition-all"
                >
                  <div className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-cyan-500/10 to-blue-600/10 border border-cyan-500/20 rounded-3xl p-8 lg:p-12">
              <Target className="w-12 h-12 text-cyan-400 mb-6" />
              <h2 className="text-2xl font-bold text-white mb-4">Our Mission</h2>
              <p className="text-gray-400 text-lg leading-relaxed">
                To develop AI-powered healthcare solutions that augment clinical
                decision-making, improve diagnostic accuracy, and ultimately save lives—while
                maintaining the highest standards of safety, privacy, and ethical AI
                development.
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-500/10 to-indigo-600/10 border border-purple-500/20 rounded-3xl p-8 lg:p-12">
              <Eye className="w-12 h-12 text-purple-400 mb-6" />
              <h2 className="text-2xl font-bold text-white mb-4">Our Vision</h2>
              <p className="text-gray-400 text-lg leading-relaxed">
                A world where every healthcare provider has access to intelligent AI tools
                that help them deliver the best possible care—making advanced medical
                insights accessible to all, regardless of geography or resources.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Our Values</h2>
            <p className="text-gray-400 text-lg">The principles that guide everything we do</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 rounded-2xl p-6 text-center hover:border-cyan-500/30 transition-all"
              >
                <div className="inline-flex p-4 rounded-full bg-cyan-500/10 mb-4">
                  <value.icon className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{value.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Experience Section */}
      <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-6">
              <Users className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 text-sm font-medium">Our Experience</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Built by Healthcare & AI Experts
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Our team brings together decades of experience from leading healthcare institutions,
              tech companies, and research organizations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {teamExperience.map((exp, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 rounded-2xl p-8 hover:border-emerald-500/30 transition-all group"
              >
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <exp.icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2">{exp.title}</h3>
                    <p className="text-gray-400 mb-4 leading-relaxed">{exp.desc}</p>
                    <div className="flex flex-wrap gap-2">
                      {exp.highlights.map((highlight, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-xs text-emerald-400"
                        >
                          <CheckCircle className="w-3 h-3" />
                          {highlight}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Future Vision / Road Ahead Section */}
      <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full mb-6">
              <Rocket className="w-4 h-4 text-purple-400" />
              <span className="text-purple-400 text-sm font-medium">The Road Ahead</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Building the Future of Healthcare AI
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Our roadmap is ambitious. Here's what we're building to revolutionize healthcare delivery.
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 lg:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-cyan-500 via-purple-500 to-pink-500 hidden md:block" />

            <div className="space-y-8">
              {futureVision.map((item, index) => (
                <div
                  key={index}
                  className={`relative md:flex items-center gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Content */}
                  <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pr-16 md:text-right' : 'md:pl-16'}`}>
                    <div className={`bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-all ${
                      index % 2 === 0 ? 'md:ml-auto' : ''
                    } max-w-lg`}>
                      <div className={`flex items-center gap-3 mb-3 ${index % 2 === 0 ? 'md:justify-end' : ''}`}>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${item.color} text-white`}>
                          {item.phase}
                        </span>
                        <span className={`text-xs font-medium ${
                          item.status === 'Live' ? 'text-emerald-400' : 
                          item.status === 'In Development' ? 'text-yellow-400' : 'text-gray-500'
                        }`}>
                          {item.status}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>

                  {/* Timeline Dot */}
                  <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 border-4 border-[#030712]" />

                  {/* Empty space for alternating layout */}
                  <div className="md:w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-br from-white/[0.05] to-transparent border border-white/10 rounded-3xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <MapPin className="w-6 h-6 text-cyan-400" />
                  <h2 className="text-2xl font-bold text-white">Our Headquarters</h2>
                </div>
                <p className="text-gray-400 text-lg mb-6">
                  Located in the heart of Chicago's business district, our headquarters
                  serves as the hub for innovation, collaboration, and healthcare
                  transformation.
                </p>
                <div className="space-y-3 text-gray-400">
                  <p className="font-medium text-white">The Garland Building</p>
                  <p>111 North Wabash Avenue, Suite 100</p>
                  <p>Chicago, IL 60602</p>
                  <p className="pt-2">
                    <a href="tel:6302378000" className="text-cyan-400 hover:underline">(630) 237-8000</a>
                  </p>
                </div>
              </div>
              <div className="h-64 lg:h-80 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center">
                <Globe className="w-24 h-24 text-cyan-400/30" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">Join Us on This Journey</h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
            Whether you're a healthcare organization looking to transform your operations,
            or a talented individual wanting to make a difference—we'd love to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl font-semibold text-white hover:shadow-lg hover:shadow-cyan-500/25 transition-all hover:-translate-y-1"
            >
              Get in Touch
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/careers"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/5 border border-white/10 rounded-xl font-semibold text-white hover:bg-white/10 transition-all"
            >
              View Careers
              <Zap className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
